import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';

interface DiffLine {
  type: 'same' | 'add' | 'del';
  text: string;
  oldNo?: number;
  newNo?: number;
}

/** Simple LCS-based line diff (good enough for everyday use). */
function diffLines(a: string, b: string): DiffLine[] {
  const A = a.split('\n');
  const B = b.split('\n');
  const m = A.length;
  const n = B.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (A[i] === B[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0,
    j = 0;
  let oldNo = 1,
    newNo = 1;
  while (i < m && j < n) {
    if (A[i] === B[j]) {
      out.push({ type: 'same', text: A[i], oldNo: oldNo++, newNo: newNo++ });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: 'del', text: A[i], oldNo: oldNo++ });
      i++;
    } else {
      out.push({ type: 'add', text: B[j], newNo: newNo++ });
      j++;
    }
  }
  while (i < m) out.push({ type: 'del', text: A[i++], oldNo: oldNo++ });
  while (j < n) out.push({ type: 'add', text: B[j++], newNo: newNo++ });
  return out;
}

const SAMPLE_A = `Hello world
This is line two
Same line
Goodbye`;

const SAMPLE_B = `Hello, world!
This is line 2
Same line
A new line
Goodbye`;

export function TextDiff() {
  const [a, setA] = useState(SAMPLE_A);
  const [b, setB] = useState(SAMPLE_B);

  const diff = useMemo(() => diffLines(a, b), [a, b]);

  const stats = useMemo(() => {
    let add = 0,
      del = 0,
      same = 0;
    for (const d of diff) {
      if (d.type === 'add') add++;
      else if (d.type === 'del') del++;
      else same++;
    }
    return { add, del, same };
  }, [diff]);

  return (
    <div>
      <ToolHeader
        toolId="text-diff"
        title="Text diff"
        description="Compare two pieces of text line by line. See additions, deletions, and unchanged lines side by side."
        badges={
          <>
            <Badge variant="success">+{stats.add}</Badge>
            <Badge variant="danger">−{stats.del}</Badge>
            <Badge>{stats.same} same</Badge>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Original</span>
            <span className="text-2xs text-fg-subtle font-mono">{a.split('\n').length} lines</span>
          </div>
          <Textarea
            mono
            value={a}
            onChange={(e) => setA(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[200px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Modified</span>
            <span className="text-2xs text-fg-subtle font-mono">{b.split('\n').length} lines</span>
          </div>
          <Textarea
            mono
            value={b}
            onChange={(e) => setB(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[200px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
          Diff
        </div>
        <div className="font-mono text-[13px] leading-relaxed">
          {diff.map((d, i) => (
            <div
              key={i}
              className={cn(
                'flex items-start gap-3 px-4 py-0.5',
                d.type === 'add' && 'bg-success/10 text-success',
                d.type === 'del' && 'bg-danger/10 text-danger',
                d.type === 'same' && 'text-fg-muted',
              )}
            >
              <span className="text-2xs text-fg-subtle w-6 text-right shrink-0 font-mono">
                {d.oldNo ?? ''}
              </span>
              <span className="text-2xs text-fg-subtle w-6 text-right shrink-0 font-mono">
                {d.newNo ?? ''}
              </span>
              <span className="w-3 shrink-0">
                {d.type === 'add' ? '+' : d.type === 'del' ? '−' : ' '}
              </span>
              <span className="break-all whitespace-pre-wrap">{d.text}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default TextDiff;
